import { powerSyncCollectionOptions } from "@tanstack/powersync-db-collection";
import { createCollection } from "@tanstack/react-db";
import { DocumentTypeSchema, idCaptureSourceValues } from "@vidimo/shared";
import { z } from "zod";
import { AppSchema } from "@/lib/powersync-schema.generated";
import { collectionBase } from "./_factory";
import { nullishToNull } from "./nullish";
import { tolerantEnumValue } from "./server-enum";
import { syncedRow } from "./synced-row";

// Tolerant reads for the two enum columns, sharing `tolerantEnumValue` with
// serverEnum / serverEnumOrNull — here they run inside the deserialization
// TRANSFORM because this collection's draft-validation schema must stay
// strict (drafts write these columns).
const idCaptureSourceEnum = z.enum(idCaptureSourceValues);

/**
 * The columns both schemas agree on, spelled once.
 *
 * A column added to the wire schema and forgotten here would be STRIPPED on
 * every `collection.update(...)` rather than rejected — the in-memory schema is
 * a plain `z.object`, so an unlisted key is silently dropped. Spreading one
 * object is what makes that impossible; the columns that genuinely differ stay
 * inline where they are declared, which is where the difference matters.
 */
const guestColumns = {
  birthCountry: z.string().nullable(),
  birthPlace: z.string().nullable(),
  createdAt: z.string(),
  dateOfBirth: z.string().nullable(),
  email: z.string().nullable(),
  firstName: z.string(),
  id: z.string(),
  idDocumentNumber: z.string().nullable(),
  idExpiryDate: z.string().nullable(),
  idIssuingCountry: z.string().nullable(),
  idVerifiedAt: z.string().nullable(),
  idVerifiedByStaffId: z.string().nullable(),
  lastName: z.string(),
  nationalIdNumber: z.string().nullable(),
  nationality: z.string().nullable(),
  phone: z.string(),
  propertyId: z.string(),
  residenceAddress: z.string().nullable(),
  residenceCountry: z.string().nullable(),
  sex: z.string().nullable(),
  updatedAt: z.string(),
};

// Deserialization schema: SQLite (string timestamps) → in-memory (Date).
// Used by powersync on incoming sync rows.
export const SelectGuestSchema = syncedRow({
  ...guestColumns,
  idCaptureSource: z.string().nullable(),
  idDocumentType: z.string().nullable(),
}).transform((row) => ({
  ...nullishToNull(row),
  // Tolerant reads, not casts: an enum value this bundle doesn't know yet
  // must not leak through typed as the union (it would corrupt Record
  // lookups) nor fail the row (a rejected row drops out of the collection).
  // Unknown capture source reads as null; unknown document type as the
  // designed catch-all.
  idCaptureSource: tolerantEnumValue(
    row.idCaptureSource,
    idCaptureSourceEnum,
    null
  ),
  idDocumentType:
    row.idDocumentType === null
      ? null
      : tolerantEnumValue(row.idDocumentType, DocumentTypeSchema, "other"),
}));

// In-memory validation schema: matches `SelectGuestSchema` 1:1, date columns
// included (see `syncedRow`). `organizationId` is intentionally absent: guests carry only `propertyId`
// as their tenant key.
export const InMemoryGuestSchema = z.object({
  ...guestColumns,
  idCaptureSource: z.enum(idCaptureSourceValues).nullable(),
  idDocumentType: DocumentTypeSchema.nullable(),
});

export type Guest = z.infer<typeof SelectGuestSchema>;

export const guestsCollection = createCollection(
  powerSyncCollectionOptions({
    ...collectionBase("guest"),
    deserializationSchema: SelectGuestSchema,
    schema: InMemoryGuestSchema,
    table: AppSchema.props.guests,
  })
);
